import React, { useState, useEffect } from 'react';
import { useGetSecureMediaUrlQuery } from '../../store/services/whatsappApi';

interface MediaMessageProps {
  mediaUrl?: string;
  mediaType: string;
  mediaMetadata?: {
    media_id?: string;
    storage_key?: string;
    media_asset_id?: number;
  };
  fileName?: string | undefined;
  caption?: string;
}

const MediaMessage: React.FC<MediaMessageProps> = ({ 
  mediaUrl, 
  mediaType, 
  mediaMetadata,
  fileName,
  caption
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [displayUrl, setDisplayUrl] = useState<string | null>(mediaUrl || null);
  
  // If we have a media asset ID, fetch the secure URL
  const { data: secureMedia } = useGetSecureMediaUrlQuery(
    mediaMetadata?.media_asset_id || 0, 
    { skip: !mediaMetadata?.media_asset_id }
  );
  
  useEffect(() => {
    if (secureMedia?.url) {
      setDisplayUrl(secureMedia.url);
      setIsLoading(false);
    }
  }, [secureMedia]);
  
  // Set initial loading state
  useEffect(() => {
    if (mediaUrl) {
      setIsLoading(true);
      
      // Determine media type from URL or provided mediaType
      const isImageType = mediaType === 'image' || (mediaUrl && mediaUrl.match(/\.(jpeg|jpg|gif|png)$/i));
      
      if (isImageType) {
        // Check if the URL is valid by creating a test image
        const img = new Image();
        img.onload = () => {
          setIsLoading(false);
          setLoadError(false);
        };
        img.onerror = () => {
          setIsLoading(false);
          setLoadError(true);
          console.error('Failed to load image:', mediaUrl);
        };
        img.src = mediaUrl;
      } else {
        // For non-image media, we can't preload easily, so just assume it's valid
        setIsLoading(false);
      }
    }
  }, [mediaUrl, mediaType]);
  
  // Handle image loading state
  const handleImageLoad = () => {
    setIsLoading(false);
    setLoadError(false);
  };
  
  // Handle image loading error
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Media failed to load:', e);
    setLoadError(true);
    setIsLoading(false);
  };
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-whatsapp-green"></div>
      </div>
    );
  }
  
  // Show error state
  if (loadError || !displayUrl) {
    return (
      <div className="flex items-center bg-gray-50 rounded-lg p-2 mb-1">
        <svg className="h-6 w-6 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span className="text-sm text-gray-700">
          {fileName || 'Media unavailable'}
        </span>
      </div>
    );
  }
  
  // Determine media type from URL or provided mediaType
  const isImage = mediaType === 'image' || (displayUrl && displayUrl.match(/\.(jpeg|jpg|gif|png)$/i));
  const isVideo = mediaType === 'video' || (displayUrl && displayUrl.match(/\.(mp4|webm|ogg|mov)$/i));
  const isAudio = mediaType === 'audio' || (displayUrl && displayUrl.match(/\.(mp3|wav|ogg|m4a)$/i));
  const isDocument = mediaType === 'document' || (displayUrl && displayUrl.match(/\.(pdf|doc|docx|xls|xlsx|txt|csv|ppt|pptx)$/i));
  
  // Render based on media type
  if (isImage) {
    return (
      <div className="rounded-lg overflow-hidden mb-1">
        <img 
          src={displayUrl} 
          alt={fileName || caption || "Image"} 
          className="max-w-full h-auto rounded" 
          loading="lazy" 
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
        {caption && (
          <div className="mt-1 text-sm">{caption}</div>
        )}
      </div>
    );
  } else if (isVideo) {
    return (
      <div className="rounded-lg overflow-hidden mb-1">
        <video 
          src={displayUrl} 
          controls 
          preload="metadata"
          poster="/video-placeholder.svg"
          className="max-w-full h-auto rounded"
          onError={(e) => {
            console.error('Video failed to load:', e, displayUrl);
            setLoadError(true);
          }}
          onLoadedMetadata={() => {
            console.log('Video metadata loaded successfully:', displayUrl);
          }}
        />
        {caption && (
          <div className="mt-1 text-sm">{caption}</div>
        )}
      </div>
    );
  } else if (isAudio) {
    return (
      <div className="rounded-lg mb-1">
        <audio 
          src={displayUrl} 
          controls 
          className="w-full"
          onError={() => setLoadError(true)}
        />
        {caption && (
          <div className="mt-1 text-sm">{caption}</div>
        )}
      </div>
    );
  } else if (isDocument) {
    // Extract file extension and name
    const fileUrl = displayUrl || '';
    const fileNameFromUrl = fileUrl.split('/').pop() || 'Document';
    const displayName = fileName || fileNameFromUrl;
    const fileExt = displayName.split('.').pop()?.toLowerCase() || '';
    
    // Determine file type and icon
    let fileType = 'Document';
    let fileIcon = (
      <svg className="h-10 w-10 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
    
    // Set file type based on extension
    if (['pdf'].includes(fileExt)) {
      fileType = 'PDF Document';
      fileIcon = (
        <svg className="h-10 w-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (['doc', 'docx'].includes(fileExt)) {
      fileType = 'Word Document';
      fileIcon = (
        <svg className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (['xls', 'xlsx', 'csv'].includes(fileExt)) {
      fileType = 'Spreadsheet';
      fileIcon = (
        <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (['ppt', 'pptx'].includes(fileExt)) {
      fileType = 'Presentation';
      fileIcon = (
        <svg className="h-10 w-10 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else if (['txt', 'rtf'].includes(fileExt)) {
      fileType = 'Text Document';
    }
    
    return (
      <div className="bg-gray-50 rounded-lg p-3 mb-1 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {fileIcon}
          </div>
          <div className="ml-3 flex-grow overflow-hidden">
            <div className="font-medium text-sm text-gray-900 truncate mb-1">
              {displayName}
            </div>
            <div className="flex items-center text-xs text-gray-500">
              <span>{fileType}</span>
              {fileExt && <span className="ml-2 uppercase bg-gray-200 px-1.5 py-0.5 rounded text-gray-600">{fileExt}</span>}
            </div>
          </div>
        </div>
        <div className="mt-3">
          {caption && (
            <div className="mb-2 text-sm text-gray-700">{caption}</div>
          )}
          <div className="flex justify-end">
            <a 
              href={displayUrl} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-whatsapp-green hover:bg-whatsapp-dark-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-whatsapp-green"
            >
              <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </a>
          </div>
        </div>
      </div>
    );
  } else {
    // Unknown media type - show link
    return (
      <div className="flex items-center bg-gray-50 rounded-lg p-2 mb-1">
        <svg className="h-6 w-6 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        <a 
          href={displayUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-sm text-blue-600 hover:underline truncate"
        >
          {fileName || 'Media attachment'}
        </a>
      </div>
    );
  }
};

export default MediaMessage;
